import React, { useState, useEffect, useMemo } from 'react';
import API_BASE_URL from "../config";
import {
  Activity, Search, RefreshCw, ChevronDown, ChevronLeft,
  ChevronRight, X, Shield, AlertCircle, Clock,
  Plus, Edit3, Trash2, Eye, LogIn, LogOut,
  CheckCircle, Download,
} from 'lucide-react';

/* ─── ACTION CONFIG — no more purple/violet ─── */
const ACTIONS = {
  CREATE:  { bg: '#dcfce7', text: '#16a34a', border: '#bbf7d0', Icon: Plus },
  ADD:     { bg: '#dcfce7', text: '#16a34a', border: '#bbf7d0', Icon: Plus },
  UPDATE:  { bg: '#dbeafe', text: '#2563eb', border: '#bfdbfe', Icon: Edit3 },
  EDIT:    { bg: '#dbeafe', text: '#2563eb', border: '#bfdbfe', Icon: Edit3 },
  DELETE:  { bg: '#fee2e2', text: '#dc2626', border: '#fecaca', Icon: Trash2 },
  REMOVE:  { bg: '#fee2e2', text: '#dc2626', border: '#fecaca', Icon: Trash2 },
  VIEW:    { bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0', Icon: Eye },
  LOGIN:   { bg: '#dbeafe', text: '#1d4ed8', border: '#bfdbfe', Icon: LogIn },   // was purple
  LOGOUT:  { bg: '#fef3c7', text: '#d97706', border: '#fde68a', Icon: LogOut },
  APPROVE: { bg: '#ccfbf1', text: '#0f766e', border: '#99f6e4', Icon: CheckCircle },
  EXPORT:  { bg: '#e0f2fe', text: '#0284c7', border: '#bae6fd', Icon: Download }, // was purple
  IMPORT:  { bg: '#e0f2fe', text: '#0284c7', border: '#bae6fd', Icon: Download }, // was purple
};
const getAction = (a = '') => {
  const key = Object.keys(ACTIONS).find(k => a.toUpperCase().includes(k));
  return ACTIONS[key] || { bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0', Icon: Activity };
};

// ✅ No more purple — all blue/teal/green/amber/red
const MOD_COLORS = ['#2563eb', '#0284c7', '#0f766e', '#d97706', '#dc2626', '#0ea5e9', '#059669'];
const modColor = (() => { const m = {}; let i = 0; return (k) => { if (!m[k]) m[k] = MOD_COLORS[i++ % MOD_COLORS.length]; return m[k]; }; })();

// ✅ No more purple gradients
const GRADS = [
  'linear-gradient(135deg,#3b82f6,#1d4ed8)',
  'linear-gradient(135deg,#0ea5e9,#0284c7)',
  'linear-gradient(135deg,#10b981,#059669)',
  'linear-gradient(135deg,#f59e0b,#d97706)',
  'linear-gradient(135deg,#ef4444,#dc2626)',
  'linear-gradient(135deg,#06b6d4,#0891b2)',
];
const avatarGrad = name => GRADS[(name || '?').charCodeAt(0) % GRADS.length];
const initials   = name => (name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

const PAGE_SIZES = [10, 25, 50, 100];

const fmtDate = d => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    + '  ' + dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const fmtRel = d => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s <    60) return `${s}s ago`;
  if (s <  3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function Avatar({ userId, fullName, username, size = 36 }) {
  const [err, setErr] = useState(false);
  const name     = fullName || username || '?';
  const fontSize = size <= 36 ? 12 : size <= 50 ? 15 : Math.round(size * 0.28);
  const shadow   = size > 50 ? '0 4px 14px rgba(0,0,0,0.18)' : '0 2px 6px rgba(0,0,0,0.14)';
  const ring     = size > 50 ? '3px solid rgba(255,255,255,0.85)' : '2px solid rgba(255,255,255,0.7)';

  if (!userId || err) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: avatarGrad(name),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize, fontWeight: 800, color: '#fff', flexShrink: 0,
        boxShadow: shadow, border: ring,
      }}>
        {initials(name)}
      </div>
    );
  }

  return (
    <img
      src={`${API_BASE_URL}api/Users/${userId}/profile`}
      alt={name}
      onError={() => setErr(true)}
      style={{
        width: size, height: size, borderRadius: '50%',
        objectFit: 'cover', flexShrink: 0,
        boxShadow: shadow, border: ring, display: 'block',
      }}
    />
  );
}

export default function LogsTable() {
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [search,    setSearch]    = useState('');
  const [actFilter, setActFilter] = useState('all');
  const [modFilter, setModFilter] = useState('all');
  const [usrFilter, setUsrFilter] = useState('all');
  const [sortDir,   setSortDir]   = useState('desc');
  const [pageSize,  setPageSize]  = useState(25);
  const [page,      setPage]      = useState(1);
  const [expanded,  setExpanded]  = useState(null);
  const [mounted,   setMounted]   = useState(false);

  useEffect(() => { fetchLogs(); setTimeout(() => setMounted(true), 80); }, []);

  const fetchLogs = async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API_BASE_URL}api/Logs`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLogs(Array.isArray(data) ? data : [data]);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const actOpts = useMemo(() => ['all', ...new Set(logs.map(l => l.action).filter(Boolean))],   [logs]);
  const modOpts = useMemo(() => ['all', ...new Set(logs.map(l => l.module).filter(Boolean))],   [logs]);
  const usrOpts = useMemo(() => ['all', ...new Set(logs.map(l => l.username).filter(Boolean))], [logs]);

  const filtered = useMemo(() => {
    let arr = [...logs];
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(l => [l.action, l.module, l.description, l.username, l.fullName].some(v => v?.toLowerCase().includes(q)));
    }
    if (actFilter !== 'all') arr = arr.filter(l => l.action   === actFilter);
    if (modFilter !== 'all') arr = arr.filter(l => l.module   === modFilter);
    if (usrFilter !== 'all') arr = arr.filter(l => l.username === usrFilter);
    return arr.sort((a, b) => {
      const da = new Date(a.createdAt || 0), db = new Date(b.createdAt || 0);
      return sortDir === 'desc' ? db - da : da - db;
    });
  }, [logs, search, actFilter, modFilter, usrFilter, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const rows       = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [search, actFilter, modFilter, usrFilter, pageSize]);

  const stats = useMemo(() => {
    const t = new Date().toDateString();
    return {
      total:   logs.length,
      today:   logs.filter(l => new Date(l.createdAt).toDateString() === t).length,
      creates: logs.filter(l => ['CREATE', 'ADD'].some(k => l.action?.toUpperCase().includes(k))).length,
      deletes: logs.filter(l => ['DELETE', 'REMOVE'].some(k => l.action?.toUpperCase().includes(k))).length,
      users:   new Set(logs.map(l => l.username).filter(Boolean)).size,
    };
  }, [logs]);

  const clearFilters = () => { setSearch(''); setActFilter('all'); setModFilter('all'); setUsrFilter('all'); };
  const hasFilter    = search || actFilter !== 'all' || modFilter !== 'all' || usrFilter !== 'all';

  const pageNums = (() => {
    const delta = 2, pages = [];
    let l = Math.max(1, page - delta), r = Math.min(totalPages, page + delta);
    if (page - delta <= 1) r = Math.min(totalPages, 1 + delta * 2);
    if (page + delta >= totalPages) l = Math.max(1, totalPages - delta * 2);
    if (l > 1) { pages.push(1); if (l > 2) pages.push('…'); }
    for (let p = l; p <= r; p++) pages.push(p);
    if (r < totalPages) { if (r < totalPages - 1) pages.push('…'); pages.push(totalPages); }
    return pages;
  })();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #eff6ff 0%, #f8fafc 50%, #f0f9ff 100%)',
      padding: '20px',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    }}>
      {/* top accent bar — blue only */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: 3,
        background: 'linear-gradient(90deg, #2563eb, #3b82f6, #0ea5e9, #38bdf8)',
        zIndex: 100,
      }} />

      <div style={{
        maxWidth: 1440, margin: '0 auto',
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity .45s ease, transform .45s ease',
      }}>

        {/* ══ HEADER ══ */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 50, height: 48, borderRadius: 14,
              background: 'linear-gradient(135deg, #2563eb, #0ea5e9)', // ✅ blue only
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(37,99,235,0.25)',
            }}>
              <Activity size={22} color="#fff" />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
                System Logs
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 3 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#16a34a',
                  background: '#dcfce7', border: '1px solid #bbf7d0',
                  padding: '2px 9px', borderRadius: 20, fontWeight: 600,
                }}>
                  <span style={{
                    width: 6, height: 6, background: '#16a34a', borderRadius: '50%', display: 'inline-block',
                    animation: 'pulse 2s ease-in-out infinite',
                  }} />
                  LIVE AUDIT TRAIL
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{logs.length.toLocaleString()} total records</span>
              </div>
            </div>
          </div>

          <button onClick={fetchLogs} disabled={loading} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 20px',
            background: '#fff', border: '1px solid #e2e8f0',
            borderRadius: 10, color: '#475569', fontSize: 13, cursor: 'pointer',
            fontFamily: 'inherit', fontWeight: 500,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'all .2s',
          }}>
            <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none', color: '#2563eb' }} />
            Refresh
          </button>
        </div>

        {/* ══ STATS ══ */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total Logs',   val: stats.total,   accent: '#2563eb', border: '#bfdbfe' },
            { label: 'Today',        val: stats.today,   accent: '#0284c7', border: '#bae6fd' }, // ✅ was purple
            { label: 'Creates',      val: stats.creates, accent: '#16a34a', border: '#bbf7d0' },
            { label: 'Deletes',      val: stats.deletes, accent: '#dc2626', border: '#fecaca' },
            { label: 'Active Users', val: stats.users,   accent: '#d97706', border: '#fde68a' },
          ].map(({ label, val, accent, border }) => (
            <div key={label} style={{
              background: '#fff', border: `1px solid ${border}`,
              borderTop: `3px solid ${accent}`, borderRadius: 14,
              padding: '16px 20px', boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
            }}>
              <p style={{ margin: '0 0 6px', fontSize: 10, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>{label}</p>
              <p style={{ margin: 0, fontSize: 30, fontWeight: 800, color: accent, fontVariantNumeric: 'tabular-nums' }}>{val.toLocaleString()}</p>
            </div>
          ))}
        </div>

        {/* ══ FILTERS ══ */}
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 14, padding: 16, marginBottom: 14,
          boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
              <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search action, module, user, description…"
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '9px 12px 9px 36px',
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  borderRadius: 10, color: '#0f172a', fontSize: 13, outline: 'none',
                  fontFamily: 'inherit', transition: 'border-color .2s, box-shadow .2s',
                }}
                onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'; }}
                onBlur={e =>  { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {[
              { val: actFilter, set: setActFilter, opts: actOpts, placeholder: '⚡ All Actions' },
              { val: modFilter, set: setModFilter, opts: modOpts, placeholder: '📦 All Modules' },
              { val: usrFilter, set: setUsrFilter, opts: usrOpts, placeholder: '👤 All Users'   },
            ].map(({ val, set, opts, placeholder }, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <select value={val} onChange={e => set(e.target.value)} style={{
                  appearance: 'none', padding: '9px 32px 9px 12px',
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  borderRadius: 10, color: '#374151', fontSize: 13, cursor: 'pointer',
                  outline: 'none', minWidth: 140, fontFamily: 'inherit',
                }}>
                  {opts.map(o => <option key={o} value={o}>{o === 'all' ? placeholder : o}</option>)}
                </select>
                <ChevronDown size={12} color="#94a3b8" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              </div>
            ))}

            <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 10, color: '#374151', fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit', whiteSpace: 'nowrap', fontWeight: 500,
            }}>
              <Clock size={13} color="#2563eb" /> {sortDir === 'desc' ? 'Newest First' : 'Oldest First'}
            </button>

            {hasFilter && (
              <button onClick={clearFilters} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px',
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 10, color: '#dc2626', fontSize: 13, cursor: 'pointer',
                fontFamily: 'inherit', fontWeight: 500,
              }}>
                <X size={13} /> Clear
              </button>
            )}
          </div>

          {hasFilter && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, paddingTop: 12,
              borderTop: '1px solid #f1f5f9',
            }}>
              <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center', fontWeight: 500 }}>Active:</span>
              {search              && <Chip c="#2563eb" l={`"${search}"`} />}
              {actFilter !== 'all' && <Chip c="#3b82f6" l={actFilter} />}
              {modFilter !== 'all' && <Chip c="#0284c7" l={modFilter} />}  {/* ✅ was purple */}
              {usrFilter !== 'all' && <Chip c="#d97706" l={usrFilter} />}
              <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>
                {filtered.length.toLocaleString()} result{filtered.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* ══ TABLE ══ */}
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 16, overflow: 'hidden', marginBottom: 14,
          boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '11px 20px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc',
          }}>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
              {filtered.length.toLocaleString()} entries · page {page}/{totalPages}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ fontSize: 12, color: '#94a3b8', marginRight: 4 }}>Rows:</span>
              {PAGE_SIZES.map(s => (
                <button key={s} onClick={() => setPageSize(s)} style={{
                  padding: '4px 10px', borderRadius: 7, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                  background: pageSize === s ? '#eff6ff' : 'transparent',
                  border: pageSize === s ? '1px solid #bfdbfe' : '1px solid transparent',
                  color: pageSize === s ? '#2563eb' : '#94a3b8',
                  fontWeight: pageSize === s ? 700 : 400,
                }}>{s}</button>
              ))}
            </div>
          </div>

          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', gap: 14 }}>
              <div style={{
                width: 36, height: 36, border: '3px solid #bfdbfe',
                borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite',
              }} />
              <span style={{ color: '#94a3b8', fontSize: 13 }}>Fetching logs…</span>
            </div>
          )}

          {!loading && error && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', gap: 12 }}>
              <AlertCircle size={40} color="#fca5a5" />
              <p style={{ color: '#dc2626', margin: 0, fontSize: 13 }}>Error: {error}</p>
              <button onClick={fetchLogs} style={{
                padding: '8px 18px', background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 10, color: '#dc2626', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
              }}>Retry</button>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', gap: 10 }}>
              <Shield size={38} color="#cbd5e1" />
              <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>No logs found</p>
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    {['#', 'Action', 'Module', 'Description', 'User', 'Timestamp', ''].map(h => (
                      <th key={h} style={{
                        padding: '10px 18px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                        color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((log, idx) => {
                    const cfg    = getAction(log.action);
                    const mc     = modColor(log.module || '');
                    const isOpen = expanded === log.id;
                    const rowN   = (page - 1) * pageSize + idx + 1;
                    const Icon   = cfg.Icon;

                    return (
                      <React.Fragment key={log.id}>
                        <tr
                          onClick={() => setExpanded(isOpen ? null : log.id)}
                          style={{
                            borderBottom: '1px solid #f8fafc', cursor: 'pointer',
                            background: isOpen ? '#eff6ff' : 'transparent',
                            transition: 'background .15s',
                          }}
                          onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#f8fafc'; }}
                          onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <td style={{ padding: '13px 18px', color: '#cbd5e1', fontSize: 12, fontWeight: 500 }}>{rowN}</td>

                          <td style={{ padding: '13px 18px', whiteSpace: 'nowrap' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px',
                              borderRadius: 8, fontSize: 11, fontWeight: 700,
                              background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`,
                            }}>
                              <Icon size={11} />{log.action || '—'}
                            </span>
                          </td>

                          <td style={{ padding: '13px 18px', whiteSpace: 'nowrap' }}>
                            <span style={{
                              fontSize: 12, fontWeight: 700, color: mc,
                              background: `${mc}12`, border: `1px solid ${mc}30`,
                              padding: '3px 9px', borderRadius: 6,
                            }}>{log.module || '—'}</span>
                          </td>

                          <td style={{ padding: '13px 18px', maxWidth: 320 }}>
                            <span style={{
                              fontSize: 13, color: '#475569', display: 'block',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }} title={log.description}>
                              {log.description || <em style={{ color: '#cbd5e1', fontStyle: 'normal' }}>—</em>}
                            </span>
                          </td>

                          <td style={{ padding: '10px 18px', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <Avatar userId={log.userId} fullName={log.fullName} username={log.username} size={38} />
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>
                                  {log.fullName || log.username || '—'}
                                </div>
                                {log.fullName && (
                                  <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.2 }}>@{log.username}</div>
                                )}
                              </div>
                            </div>
                          </td>

                          <td style={{ padding: '13px 18px', whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>{fmtDate(log.createdAt)}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{fmtRel(log.createdAt)}</div>
                          </td>

                          <td style={{ padding: '13px 12px' }}>
                            <ChevronDown size={14} color="#cbd5e1"
                              style={{ display: 'block', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .2s' }} />
                          </td>
                        </tr>

                        {/* ══ EXPANDED DETAIL ══ */}
                        {isOpen && (
                          <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#f8faff' }}>
                            <td colSpan={7} style={{ padding: '4px 20px 18px 62px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, paddingTop: 12 }}>

                                {[
                                  { label: 'Log ID',    val: `#${log.id}`,              accent: '#2563eb' },
                                  { label: 'User ID',   val: String(log.userId ?? '—'), accent: '#64748b' },
                                  { label: 'Full Name', val: log.fullName || '—',        accent: '#0284c7' }, // ✅ was purple
                                  { label: 'Username',  val: `@${log.username || '—'}`, accent: '#d97706' },
                                ].map(({ label, val, accent }) => (
                                  <div key={label} style={{
                                    background: '#fff', border: '1px solid #e2e8f0',
                                    borderRadius: 10, padding: '10px 14px',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                  }}>
                                    <div style={{ fontSize: 9, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5, fontWeight: 600 }}>{label}</div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: accent }}>{val}</div>
                                  </div>
                                ))}

                                {/* ── PROFILE CARD ── */}
                                <div style={{
                                  gridColumn: '1 / -1',
                                  background: 'linear-gradient(135deg, #f0f9ff 0%, #f8faff 100%)',
                                  border: '1px solid #bae6fd', borderRadius: 12,
                                  padding: '18px 22px',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                }}>
                                  <div style={{ fontSize: 9, color: '#0284c7', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14, fontWeight: 700 }}>
                                    👤 User Profile
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                                    <Avatar userId={log.userId} fullName={log.fullName} username={log.username} size={90} />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                      <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
                                        {log.fullName || log.username || '—'}
                                      </div>
                                      <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                                        @{log.username || '—'}
                                      </div>
                                      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                                        <span style={{
                                          fontSize: 11, fontWeight: 600, padding: '3px 12px', borderRadius: 20,
                                          background: '#e0f2fe', color: '#0284c7', border: '1px solid #bae6fd', // ✅ was purple
                                        }}>
                                          User ID: {log.userId ?? '—'}
                                        </span>
                                        <span style={{
                                          fontSize: 11, fontWeight: 600, padding: '3px 12px', borderRadius: 20,
                                          background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0',
                                        }}>
                                          ✓ Logged In
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {log.description && (
                                  <div style={{
                                    gridColumn: '1 / -1', background: '#fff',
                                    border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                  }}>
                                    <div style={{ fontSize: 9, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5, fontWeight: 600 }}>Description</div>
                                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{log.description}</div>
                                  </div>
                                )}

                                <div style={{
                                  gridColumn: '1 / -1', background: '#eff6ff',
                                  border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px',
                                }}>
                                  <div style={{ fontSize: 9, color: '#93c5fd', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5, fontWeight: 600 }}>Raw Timestamp</div>
                                  <div style={{ fontSize: 12, color: '#2563eb', fontFamily: 'monospace' }}>{log.createdAt || '—'}</div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ══ PAGINATION ══ */}
        {!loading && !error && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
              Showing {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, filtered.length).toLocaleString()} of {filtered.length.toLocaleString()}
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <PgBtn label="«" onClick={() => setPage(1)} disabled={page === 1} />
              <PgBtn label={<ChevronLeft size={13} />} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
              {pageNums.map((p, i) =>
                p === '…'
                  ? <span key={`e${i}`} style={{ padding: '6px 4px', color: '#cbd5e1', fontSize: 12 }}>…</span>
                  : <button key={p} onClick={() => setPage(p)} style={{
                      padding: '6px 12px', borderRadius: 8, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                      fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                      background: page === p ? '#2563eb' : '#fff',
                      border: page === p ? '1px solid #2563eb' : '1px solid #e2e8f0',
                      color: page === p ? '#fff' : '#475569',
                      boxShadow: page === p ? '0 2px 8px rgba(37,99,235,0.25)' : '0 1px 3px rgba(0,0,0,0.04)',
                    }}>{p}</button>
              )}
              <PgBtn label={<ChevronRight size={13} />} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
              <PgBtn label="»" onClick={() => setPage(totalPages)} disabled={page === totalPages} />
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
        * { box-sizing: border-box; }
        select option { background: #fff; color: #1e293b; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #bfdbfe; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #93c5fd; }
      `}</style>
    </div>
  );
}

function Chip({ c, l }) {
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: `${c}15`, border: `1px solid ${c}30`, color: c,
    }}>{l}</span>
  );
}

function PgBtn({ label, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '6px 10px', borderRadius: 8, fontSize: 12, fontFamily: 'inherit',
      cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center',
      background: '#fff', border: '1px solid #e2e8f0',
      color: disabled ? '#cbd5e1' : '#475569',
      opacity: disabled ? .5 : 1,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>{label}</button>
  );
}